import React, { useEffect, useState } from 'react';
import './style.css';
import { base_url } from '../../Api/apiConfig';
import { readJson, apiErrorMessage, describeHttpFailure } from '../../Api/readJson';

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif', 'image/svg+xml', 'image/bmp', 'image/avif'];

// maxImages / maxFileMb come from the admin-managed Listing Rules (passed in by the listing forms).
// When omitted no client-side limit is applied; the backend enforces the same rules.
const ImageUploader = ({ multiple = true, onChange, onImagesChange, initialImages = [], onRemove, onUploadingChange, maxImages, maxFileMb }) => {
  const [uploadedImages, setUploadedImages] = useState([]);
  const [status, setStatus] = useState('idle'); // 'idle' | 'loading' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');
  const token = localStorage.getItem('login');

  useEffect(() => {
    if (!Array.isArray(initialImages)) return;

    setUploadedImages((prev) => {
      // If user hasn't interacted yet, just set initial images
      if (!prev.length) return initialImages;

      // Otherwise merge by id (avoid duplicates) to prevent wiping user uploads
      const byId = new Map();
      [...prev, ...initialImages].forEach((img) => {
        if (img?.id != null) byId.set(img.id, img);
      });
      return Array.from(byId.values());
    });
  }, [initialImages]);

  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    if (multiple && maxImages && uploadedImages.length + files.length > maxImages) {
      setStatus('error');
      setErrorMessage(`You can upload at most ${maxImages} images (${uploadedImages.length} already added).`);
      e.target.value = '';
      return;
    }

    const invalidType = files.find((file) => !ALLOWED_IMAGE_TYPES.includes(file.type));
    if (invalidType) {
      setStatus('error');
      setErrorMessage('Only JPG, PNG, WEBP, and GIF images are allowed.');
      return;
    }

    const oversized = maxFileMb ? files.find((file) => file.size > maxFileMb * 1024 * 1024) : null;
    if (oversized) {
      setStatus('error');
      setErrorMessage(`Each image must be ${maxFileMb}MB or smaller.`);
      return;
    }

    const formData = new FormData();
    files.forEach(file => {
      formData.append("media[]", file);
    });

    setStatus('loading');
    setErrorMessage('');
    onUploadingChange?.(true);

    try {
      const response = await fetch(`${base_url}/listing/media`, {
        method: 'POST',
        headers: {
          Accept: 'application/json', // without this Laravel answers errors with an HTML page
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await readJson(response);

      if (response.ok && data?.data) {
        const incoming = (Array.isArray(data.data) ? data.data : []).map((img) => ({
          ...img,
          source: 'new',
        }));
        const updated = multiple ? [...uploadedImages, ...incoming] : incoming;
        setUploadedImages(updated);
        onChange?.(updated.map(img => img.id));
        onImagesChange?.(updated);

        // Surface backend warnings if some selected files were skipped
        if (Array.isArray(data.warnings) && data.warnings.length > 0) {
          setStatus('error');
          setErrorMessage(`Uploaded ${data.uploaded_count ?? incoming.length} of ${data.expected_count ?? files.length} images. ${data.warnings.join(' ')}`);
        } else if (data.uploaded_count && data.expected_count && data.uploaded_count < data.expected_count) {
          setStatus('error');
          setErrorMessage(`Only ${data.uploaded_count} of ${data.expected_count} images were uploaded. The rest may have failed validation or exceeded server limits.`);
        } else {
          setStatus('success');
          setTimeout(() => setStatus('idle'), 1500); // Reset after 1.5 sec
        }
      } else {
        throw new Error(apiErrorMessage(data, describeHttpFailure(response.status)));
      }
    } catch (err) {
      console.error("Image upload failed", err);
      setStatus('error');
      setErrorMessage(err.message || 'Upload error');
    } finally {
      onUploadingChange?.(false);
    }
  };

  const removeImage = async (index) => {
    const target = uploadedImages[index];

    // Allow parent to veto removal (e.g. server-side delete failed)
    if (typeof onRemove === 'function') {
      try {
        const result = await onRemove(target);
        if (result === false) return;
      } catch (err) {
        console.error('onRemove failed', err);
        return;
      }
    }

    const updated = uploadedImages.filter((_, i) => i !== index);
    setUploadedImages(updated);
    onChange?.(updated.map(img => img.id));
    onImagesChange?.(updated);
  };

  const getButtonText = () => {
    switch (status) {
      case 'loading': return 'Uploading...';
      case 'success': return 'Uploaded ✅';
      case 'error': return errorMessage || 'Upload Failed ❌';
      default: return '📤 Select and upload';
    }
  };

  return (
    <div className="image-upload-wrapper">
      <div className="upload-area">
        <div className="upload-content">
          {/* <div className="upload-icon">
            <img src="https://img.icons8.com/ios-filled/50/image.png" alt="upload" />
          </div> */}
          <p>Upload images to customize the gallery order.</p>
          {/* <p>Click on the star icon to set the featured image</p> */}
          <p className="text-muted small">Maximum Filesize: 10MB</p>
          <label className={`upload-btn ${status}`}>
            <input
              type="file"
              multiple={multiple}
              accept="image/jpeg,image/jpg,image/png,image/webp,image/gif,image/svg+xml,image/bmp,image/avif"
              onChange={handleImageChange}
              hidden
            />
            <span>{getButtonText()}</span>
          </label>
        </div>
      </div>

      <div className="preview-area mt-3">
        {uploadedImages.map((img, index) => (
          <div key={img.id || index} className="preview-box">
            <img src={img.thumbnail_url || img.image_url} alt={`uploaded-${index}`} />
            <button
              type="button"
              className="remove-btn"
              onClick={() => removeImage(index)}
              title="Remove"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ImageUploader;
